/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { MissionRoleType, OperationType } from 'src/graphql/generated/graphql.generated';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface UnitActivationRuleProps {
  id: string;
  companyId: string;
  unitId: string;
  operationTypes: Set<OperationType>;
  missionRoleTypes: Set<MissionRoleType>;
  maximumDistanceInMiles: number | null;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class UnitActivationRuleRecord extends Record<UnitActivationRuleProps>({
  id: null as any,
  companyId: null as any,
  unitId: null as any,
  operationTypes: null as any,
  missionRoleTypes: null as any,
  maximumDistanceInMiles: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    companyId: string;
    unitId: string;
    operationTypes: any;
    missionRoleTypes: any;
    maximumDistanceInMiles: number | null;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): UnitActivationRuleRecord {
    if (json == null) {
      throw new Error('UnitActivationRuleRecord.fromJS: parameter cannot be null');
    }
    return new UnitActivationRuleRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'UnitActivationRuleJson.id',
      ),
      companyId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
        'UnitActivationRuleJson.companyId',
      ),
      unitId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'unitId', '00000000-0000-0000-0000-000000000000'),
        'UnitActivationRuleJson.unitId',
      ),
      operationTypes: Set<OperationType>(
        getFromOrThrow(json, (j) => j.operationTypes, 'UnitActivationRuleJson.operationTypes'),
      ),
      missionRoleTypes: Set<MissionRoleType>(
        getFromOrThrow(json, (j) => j.missionRoleTypes, 'UnitActivationRuleJson.missionRoleTypes'),
      ),
      maximumDistanceInMiles: json.maximumDistanceInMiles,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    companyId: string;
    unitId: string;
    operationTypes: any;
    missionRoleTypes: any;
    maximumDistanceInMiles: number | null;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [UnitActivationRuleRecord, Error[]] {
    if (json == null) {
      throw new Error('UnitActivationRuleRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new UnitActivationRuleRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'UnitActivationRuleJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      companyId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
            'UnitActivationRuleJson.companyId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      unitId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'unitId', '00000000-0000-0000-0000-000000000000'),
            'UnitActivationRuleJson.unitId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      operationTypes: tryOrDefault(
        () =>
          Set<OperationType>(
            getFromOrThrow(json, (j) => j.operationTypes, 'UnitActivationRuleJson.operationTypes'),
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      missionRoleTypes: tryOrDefault(
        () =>
          Set<MissionRoleType>(
            getFromOrThrow(
              json,
              (j) => j.missionRoleTypes,
              'UnitActivationRuleJson.missionRoleTypes',
            ),
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      maximumDistanceInMiles: tryOrDefault(
        () => json.maximumDistanceInMiles,
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: UnitActivationRuleProps) {
    super(props);
    if (props == null) {
      throw new Error('UnitActivationRuleRecord constructor parameter cannot be null');
    }
  }
}

export abstract class UnitActivationRuleViewModelBase implements UnitActivationRuleProps {
  protected readonly _props: UnitActivationRuleProps;
  private _json?: UnitActivationRuleProps;

  get id(): string {
    return this._props.id;
  }
  get companyId(): string {
    return this._props.companyId;
  }
  get unitId(): string {
    return this._props.unitId;
  }
  get operationTypes(): Set<OperationType> {
    return this._props.operationTypes;
  }
  get missionRoleTypes(): Set<MissionRoleType> {
    return this._props.missionRoleTypes;
  }
  get maximumDistanceInMiles(): number | null {
    return this._props.maximumDistanceInMiles;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  constructor(props: UnitActivationRuleProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        companyId: this._props.companyId,
        unitId: this._props.unitId,
        operationTypes: this._props.operationTypes,
        missionRoleTypes: this._props.missionRoleTypes,
        maximumDistanceInMiles: this._props.maximumDistanceInMiles,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllUnitActivationRuleEntities = (state: AppState) =>
  state.entityStores.unitActivationRule.entities;

export const selectUnitActivationRuleById = (id: string) => (state: AppState) =>
  state.entityStores.unitActivationRule.entities.get(id);

export const selectUnitActivationRuleMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.unitActivationRule.meta.get(id);

export const selectUnitActivationRuleAndMetadataById = (id: string) =>
  createSelector(
    selectUnitActivationRuleById(id),
    selectUnitActivationRuleMetadataById(id),
    (unitActivationRule, meta) => ({ unitActivationRule, meta }),
  );

export function selectUnitActivationRuleEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllUnitActivationRuleEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, UnitActivationRuleRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectUnitActivationRuleEntitiesByCompanyId(companyId: string) {
  return createSelector(selectAllUnitActivationRuleEntities, (entities) =>
    entities.filter((e) => e.companyId === companyId).valueSeq(),
  );
}

export function selectUnitActivationRuleEntitiesByUnitId(unitId: string) {
  return createSelector(selectAllUnitActivationRuleEntities, (entities) =>
    entities.filter((e) => e.unitId === unitId).valueSeq(),
  );
}

export function selectUnitActivationRuleEntitiesByMaximumDistanceInMiles(
  maximumDistanceInMiles: number,
) {
  return createSelector(selectAllUnitActivationRuleEntities, (entities) =>
    entities.filter((e) => e.maximumDistanceInMiles === maximumDistanceInMiles).valueSeq(),
  );
}
//#endregion Selectors
